import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Address, formatUnits, isAddress } from 'viem';
import { mainnet } from 'viem/chains';
import { useConnection, useReadContracts } from 'wagmi';

import { DASH, MZUSD_DECIMALS, TRANCHE_SHARE_DECIMALS } from '@/app/shared/constants/common';
import { Interval } from '@/app/shared/constants/interval';
import { QueryKeys } from '@/app/shared/constants/queryKeys';
import {
  getTrancheAddressByLabel,
  getVaultContracts,
} from '@/app/shared/constants/web3/chainContracts';
import { ABI } from '@/app/shared/constants/web3/constants';
import {
  formatTokenAmount,
  formatUnlockEta,
  formatUsd,
  toBigInt,
  toBigIntString,
} from '@/app/shared/lib/formatters';
import { getUserPendingWithdrawals } from '@/app/shared/services/api';
import { UserPendingWithdrawal } from '@/app/shared/services/api/types';

export type WithdrawalStatus = 'Claimable' | 'Unlocking';

export interface PendingWithdrawalRow {
  id: string;
  name: string;
  tranche: string;
  amount: string;
  amountUsd: string;
  amountRaw: string;
  amountBN: bigint;
  shares: string;
  sharesBN: bigint;
  unlockEta: string;
  status: WithdrawalStatus;
  epochId?: string;
  trancheAddress?: Address;
  vaultAddress?: Address;
  canClaim: boolean;
}

type PendingWithdrawalBaseRow = Omit<PendingWithdrawalRow, 'amount' | 'amountUsd' | 'amountBN' | 'canClaim'> & {
  apiAmountBN: bigint;
  apiValueUsd?: string | number;
};

const resolveStatus = (status?: string): WithdrawalStatus => {
  const normalized = status?.toLowerCase() ?? '';
  if (normalized === 'claimable') return 'Claimable';
  return 'Unlocking';
};

const resolveEpochId = (item: UserPendingWithdrawal): string | undefined => {
  if (item.targetEpoch == null || item.targetEpoch === '') return undefined;
  return String(item.targetEpoch);
};

const resolveVaultAddress = (item: UserPendingWithdrawal): Address | undefined => {
  if (item.vaultAddress && isAddress(item.vaultAddress)) return item.vaultAddress;
  return undefined;
};

const resolveTrancheAddress = (
  item: UserPendingWithdrawal,
  vaultAddress: Address | undefined,
  chainId?: number,
): Address | undefined => {
  if (item.trancheAddress && isAddress(item.trancheAddress)) return item.trancheAddress;
  if (!vaultAddress) return undefined;

  const contracts =
    getVaultContracts(chainId, vaultAddress) ?? getVaultContracts(mainnet.id, vaultAddress);
  if (!contracts) return undefined;

  return getTrancheAddressByLabel(contracts, item.trancheName);
};

const mapWithdrawalToBaseRow = (
  item: UserPendingWithdrawal,
  index: number,
  chainId?: number,
): PendingWithdrawalBaseRow => {
  const status = resolveStatus(item.status);
  const epochId = resolveEpochId(item);
  const vaultAddress = resolveVaultAddress(item);
  const trancheAddress = resolveTrancheAddress(item, vaultAddress, chainId);
  const sharesRaw = toBigIntString(item.shares);
  const amountRaw = toBigIntString(item.amount) ?? '0';
  const apiAmountBN = toBigInt(amountRaw);
  const sharesBN = toBigInt(sharesRaw ?? amountRaw);

  return {
    id: `${vaultAddress ?? item.vaultName}-${item.trancheName}-${item.requestedAt}-${index}`,
    name: item.vaultName || DASH,
    tranche: item.trancheName || DASH,
    amountRaw,
    shares: item.shares == null ? DASH : formatTokenAmount(item.shares, TRANCHE_SHARE_DECIMALS),
    sharesBN,
    unlockEta: formatUnlockEta(item.unlockETA),
    status,
    epochId,
    trancheAddress,
    vaultAddress,
    apiAmountBN,
    apiValueUsd: item.valueUsd,
  };
};

const resolveAmountDisplay = (
  assetsBN: bigint,
  apiValueUsd?: string | number,
): { amount: string; amountUsd: string } => {
  if (assetsBN <= 0n) {
    return { amount: DASH, amountUsd: formatUsd(apiValueUsd) };
  }

  const assetsHuman = formatUnits(assetsBN, MZUSD_DECIMALS);
  const amount = formatTokenAmount(assetsBN.toString(), MZUSD_DECIMALS);
  const apiUsd = formatUsd(apiValueUsd);

  return {
    amount,
    amountUsd: apiUsd === DASH ? formatUsd(assetsHuman) : apiUsd,
  };
};

export const usePendingWithdrawals = () => {
  const { address, chainId } = useConnection();

  const { data, isLoading, error } = useQuery({
    queryKey: [QueryKeys.USER_PENDING_WITHDRAWALS, address],
    queryFn: () => getUserPendingWithdrawals({ address }),
    enabled: !!address,
    staleTime: Interval.ONE_MIN,
    refetchInterval: Interval.ONE_MIN,
  });

  const baseRows = useMemo(
    () => (data ?? []).map((item, index) => mapWithdrawalToBaseRow(item, index, chainId)),
    [data, chainId],
  );

  const conversionTargets = useMemo(
    () =>
      baseRows.filter(
        (row): row is PendingWithdrawalBaseRow & { trancheAddress: Address } =>
          row.apiAmountBN <= 0n && !!row.trancheAddress && row.sharesBN > 0n,
      ),
    [baseRows],
  );

  const { data: conversionResults, isLoading: isConversionLoading } = useReadContracts({
    contracts: conversionTargets.map((row) => ({
      address: row.trancheAddress,
      abi: ABI.TRANCHE,
      functionName: 'convertToAssets' as const,
      args: [row.sharesBN] as const,
    })),
    query: {
      enabled: conversionTargets.length > 0,
      staleTime: Interval.ONE_MIN,
      refetchInterval: Interval.ONE_MIN,
    },
  });

  const convertedById = useMemo(() => {
    const map = new Map<string, bigint>();
    conversionTargets.forEach((row, index) => {
      const result = conversionResults?.[index]?.result;
      if (typeof result === 'bigint' && result > 0n) {
        map.set(row.id, result);
      }
    });
    return map;
  }, [conversionResults, conversionTargets]);

  const rows = useMemo((): PendingWithdrawalRow[] => {
    return baseRows.map((row) => {
      const amountBN = row.apiAmountBN > 0n ? row.apiAmountBN : (convertedById.get(row.id) ?? 0n);
      const { amount, amountUsd } = resolveAmountDisplay(amountBN, row.apiValueUsd);

      return {
        id: row.id,
        name: row.name,
        tranche: row.tranche,
        amount,
        amountUsd,
        amountRaw: amountBN > 0n ? amountBN.toString() : row.amountRaw,
        amountBN,
        shares: row.shares,
        sharesBN: row.sharesBN,
        unlockEta: row.unlockEta,
        status: row.status,
        epochId: row.epochId,
        trancheAddress: row.trancheAddress,
        vaultAddress: row.vaultAddress,
        canClaim:
          row.status === 'Claimable' &&
          !!row.epochId &&
          !!row.trancheAddress &&
          !!row.vaultAddress &&
          amountBN > 0n,
      };
    });
  }, [baseRows, convertedById]);

  return {
    rows,
    count: rows.length,
    isLoading: isLoading || (conversionTargets.length > 0 && isConversionLoading),
    error,
    isConnected: !!address,
  };
};
